import { useCallback, useMemo, useState } from 'react';

import {
  useLazyGetClientByIdQuery,
  useUpdateClientMutation,
} from '@app/api/Client/api';
import type { UpdateClientPayload } from '@app/api/Client/types';
import { useCheckClientForDuplicateMutation } from '@app/api/ClientsMerge';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NClients } from '@app/types';

export type LeadClientPatch = Partial<
  Pick<
    UpdateClientPayload,
    | 'Phone1'
    | 'Phone1CountryId'
    | 'Phone2'
    | 'Phone2CountryId'
    | 'IsPhoneVerified'
    | 'Comment'
  >
>;

// UpdateClient overwrites every field it receives, so the payload is built
// from the freshly loaded client and only the edited fields are replaced.
// Null leaves tags unchanged, including changes made after this client load.
const toUpdateClientPayload = (
  client: NClients.ModelClient,
  patch: LeadClientPatch
): UpdateClientPayload => ({
  Id: client.Id,
  NameLast: client.NameLast,
  NameFirst: client.NameFirst,
  NameMiddle: client.NameMiddle,
  Gender: client.Gender,
  Email: client.Email,
  Phone1: client.Phone1,
  Phone1CountryId: client.Phone1CountryId,
  Phone2: client.Phone2,
  Phone2CountryId: client.Phone2CountryId,
  DateBirth: client.DateBirth,
  IsPhoneVerified: client.IsPhoneVerified,
  Photo: client.Photo,
  LanguageId: client.LanguageId,
  ClientTags: null,
  Comment: client.Comment,
  CompanyName: client.CompanyName,
  IsHuman: client.IsHuman,
  LeadId: client.LeadId,
  Snils: client.Snils,
  IdentityDocumentType: client.IdentityDocumentType,
  IdentityDocumentNumber: client.IdentityDocumentNumber,
  RegistrationAddress: client.RegistrationAddress,
  ...patch,
});

// Quick edits of the lead's client right in the lead card details (phone,
// note). Requires the client edit right, like the client edit page.
export const useLeadClientEditing = () => {
  const { activeBranchId } = useUserPrint();
  const { userAccessRights } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const { leadCardData, knownClientId, refetchLeadCard } = useLeadCardContext();

  const [getClientById] = useLazyGetClientByIdQuery();
  const [updateClient] = useUpdateClientMutation();
  const [checkClientForDuplicate] = useCheckClientForDuplicateMutation();
  const [isSaving, setIsSaving] = useState(false);

  // Same formula as the other lead card actions: for merged cards the client
  // from the lead response may differ from the one the parent works with.
  const clientId = useMemo(
    () =>
      knownClientId ??
      leadCardData?.ClientId ??
      leadCardData?.ClientView?.Id ??
      null,
    [knownClientId, leadCardData?.ClientId, leadCardData?.ClientView?.Id]
  );

  const canEditClient =
    Boolean(clientId) && userAccessRights?.Clients?.IsEdit === true;

  const fetchClient =
    useCallback(async (): Promise<NClients.ModelClient | null> => {
      if (!clientId || !activeBranchId) {
        showNotification('Не удалось определить клиента', FRONT_WARNING);
        return null;
      }

      try {
        return await getClientById({
          ActiveBranchId: activeBranchId,
          clientId,
        }).unwrap();
      } catch (error) {
        showNotification(error, WARNING);
        return null;
      }
    }, [activeBranchId, clientId, getClientById, showNotification]);

  const saveClientPatch = useCallback(
    async (
      patch: LeadClientPatch,
      successMessage: string
    ): Promise<boolean> => {
      setIsSaving(true);

      try {
        const client = await fetchClient();

        if (!client || !activeBranchId) return false;

        // The main phone takes part in the duplicate search — check it the
        // same way the client form does before saving.
        if (patch.Phone1) {
          const checkResult = await checkClientForDuplicate({
            ActiveBranchId: activeBranchId,
            data: { ClientId: client.Id, ClientPhone: patch.Phone1 },
          }).unwrap();

          if (checkResult.IsDuplicated) {
            showNotification(
              'Клиент с таким номером уже есть — номер не сохранён',
              WARNING
            );
            return false;
          }
        }

        await updateClient({
          ActiveBranchId: activeBranchId,
          data: toUpdateClientPayload(client, patch),
        }).unwrap();

        refetchLeadCard();
        showNotification(successMessage, SUCCESS);

        return true;
      } catch (error) {
        showNotification(error, WARNING);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [
      activeBranchId,
      checkClientForDuplicate,
      fetchClient,
      refetchLeadCard,
      showNotification,
      updateClient,
    ]
  );

  return {
    canEditClient,
    isSaving,
    fetchClient,
    saveClientPatch,
  };
};
